import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { get, put, post } from '@/shared/api/api_client';
import { cn } from '@/shared/utils/cn';
import { formatRelativeTime } from '@/shared/utils/format';
import type { AppNotification } from '@/entities/notification';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface UnreadCountResponse {
  count: number;
}

interface NotificationsResponse {
  notifications: AppNotification[];
}

interface NotificationBellProps {
  familyId: string;
}

// ---------------------------------------------------------------------------
// Route map for notification types
// ---------------------------------------------------------------------------
const notificationRouteMap: Record<string, string> = {
  chore_submitted: '/chores',
  chore_approved: '/chores',
  chore_rejected: '/chores',
  redemption_requested: '/rewards',
  redemption_approved: '/rewards',
  redemption_rejected: '/rewards',
  member_joined: '/settings',
};

// ---------------------------------------------------------------------------
// NotificationBell component
// ---------------------------------------------------------------------------
export default function NotificationBell({ familyId }: NotificationBellProps): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch unread count (poll every 30s)
  const { data: unreadData } = useQuery<UnreadCountResponse>({
    queryKey: ['notifications', familyId, 'unread-count'],
    queryFn: () => get<UnreadCountResponse>(`/families/${familyId}/notifications/unread-count`),
    refetchInterval: 30_000,
  });

  // Fetch latest notifications (only when dropdown is open)
  const { data: notificationsData, isLoading: notificationsLoading } = useQuery<NotificationsResponse>({
    queryKey: ['notifications', familyId, 'list'],
    queryFn: () => get<NotificationsResponse>(`/families/${familyId}/notifications`),
    enabled: isOpen,
  });

  // Mark single notification as read
  const markReadMutation = useMutation({
    mutationFn: (notificationId: string) =>
      put<void>(`/families/${familyId}/notifications/${notificationId}/read`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications', familyId] });
    },
  });

  // Mark all as read
  const markAllReadMutation = useMutation({
    mutationFn: () => post<void>(`/families/${familyId}/notifications/mark-all-read`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications', familyId] });
    },
  });

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleNotificationClick = useCallback(
    (notification: AppNotification) => {
      if (!notification.isRead) {
        markReadMutation.mutate(notification.id);
      }
      const route = notificationRouteMap[notification.type] ?? '/dashboard';
      setIsOpen(false);
      navigate(route);
    },
    [markReadMutation, navigate],
  );

  const unreadCount = unreadData?.count ?? 0;
  const notifications = notificationsData?.notifications ?? [];

  return (
    <div className="relative">
      {/* Bell button */}
      <button
        ref={buttonRef}
        type="button"
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className={cn(
          'relative flex h-10 w-10 items-center justify-center rounded-lg text-slate-500',
          'hover:bg-slate-100 hover:text-slate-700',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
          'transition-colors duration-150',
        )}
      >
        <Bell size={20} aria-hidden="true" />
        {unreadCount > 0 && (
          <span
            role="status"
            aria-label={`${unreadCount} unread notifications`}
            className={cn(
              'absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center',
              'rounded-full bg-danger-500 px-1 text-[10px] font-bold text-white',
            )}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className={cn(
            'absolute right-0 top-12 z-50 w-80 rounded-xl border border-slate-200 bg-white shadow-card-md',
            'animate-[slideUp_150ms_ease-out]',
          )}
          role="region"
          aria-label="Notifications"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-slate-800">Notifications</h3>
            {notifications.some((n) => !n.isRead) && (
              <button
                type="button"
                onClick={() => markAllReadMutation.mutate()}
                disabled={markAllReadMutation.isPending}
                className={cn(
                  'text-xs font-medium text-primary-600',
                  'hover:text-primary-800',
                  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-500 rounded',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                )}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Content */}
          <div className="max-h-80 overflow-y-auto">
            {notificationsLoading ? (
              <div className="space-y-3 p-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="h-8 w-8 animate-pulse rounded-full bg-slate-100" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3 w-full animate-pulse rounded bg-slate-100" />
                      <div className="h-2.5 w-24 animate-pulse rounded bg-slate-100" />
                    </div>
                  </div>
                ))}
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-8 px-4 text-center">
                <Bell size={28} className="text-slate-300" aria-hidden="true" />
                <p className="text-sm text-slate-500">You're all caught up!</p>
              </div>
            ) : (
              <ul role="list">
                {notifications.map((notification) => (
                  <li key={notification.id}>
                    <button
                      type="button"
                      onClick={() => handleNotificationClick(notification)}
                      className={cn(
                        'w-full px-4 py-3 text-left transition-colors duration-100',
                        'hover:bg-slate-50 focus-visible:bg-slate-50',
                        'focus-visible:outline-none focus-visible:ring-inset focus-visible:ring-1 focus-visible:ring-primary-500',
                        !notification.isRead && 'bg-primary-50/50',
                      )}
                    >
                      <div className="flex items-start gap-3">
                        {!notification.isRead && (
                          <span
                            className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary-500"
                            aria-hidden="true"
                          />
                        )}
                        {notification.isRead && <span className="mt-1.5 h-2 w-2 shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <p
                            className={cn(
                              'text-sm text-slate-700 leading-snug',
                              !notification.isRead && 'font-medium text-slate-900',
                            )}
                          >
                            {notification.message}
                          </p>
                          <p className="mt-0.5 text-xs text-slate-400">
                            {formatRelativeTime(notification.createdAt)}
                          </p>
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
